(function () {
  'use strict';

  function createController(config, transport) {
    const eligibleIds = new Set(config.eligibleProductIds.map(String));
    const isTote = (item) => String(item.variant_id) === String(config.variantId);
    const isManaged = (item) => isTote(item) && item.properties?.[config.propertyKey] === config.propertyValue;
    const managedItems = (cart) => cart.items.filter(isManaged);
    let failedSignature = '';

    function qualifies(cart) {
      if (cart.currency !== config.currency) return false;
      return cart.items.reduce((amount, item) => {
        if (isManaged(item) || item.gift_card || item.selling_plan_allocation || !eligibleIds.has(String(item.product_id))) return amount;
        return amount + Number(item.original_line_price);
      }, 0) >= config.minimumCents;
    }

    function hasFreeTote(cart) {
      return cart.items.some((item) => {
        if (!isTote(item) || item.selling_plan_allocation || Number(item.original_price) <= 0) return false;
        const allocated = (item.line_level_discount_allocations || []).reduce((amount, allocation) => {
          return amount + (allocation.discount_application?.title === config.discountTitle ? Number(allocation.amount) : 0);
        }, 0);
        return allocated >= Number(item.original_price);
      });
    }

    function signature(cart) {
      return JSON.stringify([cart.currency, cart.items.filter((item) => !isManaged(item)).map((item) => [
        item.variant_id, item.quantity, item.original_line_price, item.final_line_price,
        item.properties, item.selling_plan_allocation?.selling_plan?.id || null,
      ])]);
    }

    function purchaseSignature(cart) {
      return JSON.stringify([cart.currency, cart.items.filter((item) => !isManaged(item)).map((item) => [
        item.variant_id, item.quantity, item.original_line_price, item.properties,
        item.selling_plan_allocation?.selling_plan?.id || null,
      ])]);
    }

    async function removeManaged(cart) {
      for (let attempt = 0; attempt < 8; attempt += 1) {
        const item = managedItems(cart)[0];
        if (!item) return cart;
        await transport.change(item.key, 0);
        cart = await transport.read();
      }
      throw new Error('The automatic tote could not be removed.');
    }

    async function reconcile() {
      let cart = await transport.read();
      let changed = false;
      let owned = managedItems(cart);

      if (!qualifies(cart)) {
        transport.finish?.();
        failedSignature = '';
        if (owned.length) {
          cart = await removeManaged(cart);
          changed = true;
        }
        return { cart, changed, added: false };
      }

      if (owned.length && owned.reduce((quantity, item) => quantity + item.quantity, 0) !== 1) {
        cart = await removeManaged(cart);
        changed = true;
        owned = [];
      }

      if (hasFreeTote(cart)) {
        const pending = transport.pending?.();
        const carried = owned.length > 0 && pending && pending.token === cart.token &&
          pending.expiresAt > Date.now() && pending.signature === purchaseSignature(cart);
        if (carried && Number(cart.total_price) > pending.previousTotal) {
          cart = await removeManaged(cart);
          transport.finish?.();
          failedSignature = signature(cart);
          return { cart, changed: true, added: false };
        }
        if (!owned.length) transport.finish?.();
        return { cart, changed, added: Boolean(carried) };
      }

      if (owned.length) {
        cart = await removeManaged(cart);
        transport.finish?.();
        failedSignature = signature(cart);
        return { cart, changed: true, added: false };
      }

      const before = signature(cart);
      if (failedSignature === before) return { cart, changed, added: false };
      const previousTotal = Number(cart.total_price);
      transport.begin?.({ token: cart.token, signature: purchaseSignature(cart), previousTotal, expiresAt: Date.now() + 120000 });

      try {
        await transport.add(config.variantId, { [config.propertyKey]: config.propertyValue });
        cart = await transport.read();
      } catch (error) {
        cart = await transport.read();
        const safe = qualifies(cart) && hasFreeTote(cart) && Number(cart.total_price) <= previousTotal &&
          managedItems(cart).reduce((quantity, item) => quantity + item.quantity, 0) === 1;
        if (safe) return { cart, changed: true, added: true };
        if (managedItems(cart).length) cart = await removeManaged(cart);
        transport.finish?.();
        failedSignature = before;
        return { cart, changed: true, added: false };
      }

      const safe = qualifies(cart) && hasFreeTote(cart) && Number(cart.total_price) <= previousTotal &&
        managedItems(cart).reduce((quantity, item) => quantity + item.quantity, 0) === 1;

      if (!safe) {
        cart = await removeManaged(cart);
        transport.finish?.();
        failedSignature = before;
        return { cart, changed: true, added: false };
      }

      failedSignature = '';
      return { cart, changed: true, added: true };
    }

    return { reconcile, qualifies, hasFreeTote, isManaged };
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { createController };
    return;
  }

  const configElement = document.getElementById('solid-olive-free-tote-config');
  if (!configElement || window.solidOliveFreeTote) return;
  const config = JSON.parse(configElement.textContent);
  if (!config.enabled || window.Shopify?.designMode) return;

  const originalFetch = window.fetch.bind(window);
  const root = window.Shopify?.routes?.root || '/';
  const toast = document.getElementById('solid-olive-free-tote-notice');
  const errorNotice = document.getElementById('solid-olive-free-tote-error');
  let activeRequests = 0;
  let revision = 0;
  let timer;
  let running;
  let repeat = false;
  let passingCheckout = false;
  let checkoutPending = false;
  let externalRefreshPending = false;
  let refreshRequired = config.initialItemCount > 0;
  let pendingAnnouncement = false;
  const idleWaiters = new Set();
  const inertElements = new Map();
  const pendingKey = 'solid-olive-free-tote-pending-v1';

  function readPending() {
    try {
      const pending = JSON.parse(window.sessionStorage.getItem(pendingKey));
      return pending?.expiresAt > Date.now() ? pending : null;
    } catch (error) { return null; }
  }

  function clearPending() {
    try { window.sessionStorage.removeItem(pendingKey); } catch (error) {}
  }

  async function jsonRequest(path, body) {
    const response = await originalFetch(root + path, {
      method: body ? 'POST' : 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : {}),
      signal: AbortSignal.timeout(15000),
    });
    const data = await response.json();
    if (!response.ok || data.status >= 400) throw new Error(data.description || data.message || 'Cart request failed.');
    return data;
  }

  const controller = createController(config, {
    read: () => jsonRequest('cart.js'),
    add: (id, properties) => jsonRequest('cart/add.js', { items: [{ id, quantity: 1, properties }] }),
    change: (id, quantity) => jsonRequest('cart/change.js', { id, quantity }),
    begin: (pending) => {
      try { window.sessionStorage.setItem(pendingKey, JSON.stringify(pending)); } catch (error) {}
    },
    pending: readPending,
    finish: clearPending,
  });

  function setBusy(busy) {
    document.documentElement.toggleAttribute('data-solid-olive-tote-busy', busy);
    if (busy) {
      document.querySelectorAll('cart-items, cart-drawer-items, .cart__dynamic-checkout-buttons, .additional-checkout-buttons').forEach((element) => {
        if (!inertElements.has(element)) inertElements.set(element, element.inert);
        element.inert = true;
        element.setAttribute('aria-busy', 'true');
      });
    } else {
      inertElements.forEach((wasInert, element) => {
        element.inert = wasInert;
        element.removeAttribute('aria-busy');
      });
      inertElements.clear();
    }
  }

  async function refreshCart(cart) {
    const items = document.getElementById('main-cart-items');
    const footer = document.getElementById('main-cart-footer');
    const drawer = document.querySelector('cart-drawer');
    const sections = ['cart-icon-bubble'];
    if (items) sections.push(items.dataset.id);
    if (footer) sections.push(footer.dataset.id);
    if (drawer) sections.push('cart-drawer');
    const refreshed = await jsonRequest('cart/update.js', {
      updates: {},
      sections: [...new Set(sections)],
      sections_url: window.location.pathname,
    });
    const html = refreshed.sections;
    if (!html || !Array.isArray(refreshed.items)) throw new Error('Cart display could not be refreshed.');
    cart = refreshed;
    const parser = new DOMParser();
    const replacements = [];
    for (const [element, key, selector] of [[items, items?.dataset.id, '.js-contents'], [footer, footer?.dataset.id, '.js-contents']]) {
      if (!element) continue;
      if (!html[key]) throw new Error('Cart display is incomplete.');
      const source = parser.parseFromString(html[key], 'text/html').querySelector(selector);
      const target = element.querySelector(selector);
      if (source && target) {
        replacements.push([target, source.innerHTML]);
      } else {
        const wrapper = parser.parseFromString(html[key], 'text/html').querySelector('#' + element.id);
        if (!wrapper) throw new Error('Cart display is incomplete.');
        replacements.push([element, wrapper.innerHTML]);
      }
    }
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble && html['cart-icon-bubble']) {
      const source = parser.parseFromString(html['cart-icon-bubble'], 'text/html').querySelector('.shopify-section');
      if (source) replacements.push([bubble, source.innerHTML]);
    }
    if (drawer) {
      if (!html['cart-drawer']) throw new Error('Cart drawer is incomplete.');
      const source = parser.parseFromString(html['cart-drawer'], 'text/html').querySelector('#CartDrawer');
      const target = drawer.querySelector('#CartDrawer');
      if (!source || !target) throw new Error('Cart drawer is incomplete.');
      replacements.push([target, source.innerHTML]);
    }
    replacements.forEach(([element, content]) => { element.innerHTML = content; });
    if (drawer) drawer.classList.toggle('wt-cart--empty', cart.item_count === 0);
    document.querySelectorAll('cart-items, #main-cart-footer, cart-drawer').forEach((element) => element.classList.toggle('is-empty', cart.item_count === 0));
    document.dispatchEvent(new CustomEvent('solid-olive-tote:updated', { detail: { cart } }));
    return refreshed;
  }

  function waitForIdle() {
    return activeRequests ? new Promise((resolve) => idleWaiters.add(resolve)) : Promise.resolve();
  }

  async function sync() {
    clearTimeout(timer);
    if (running) {
      repeat = true;
      return running;
    }
    running = (async () => {
      let announce = pendingAnnouncement;
      let needsRefresh = externalRefreshPending || refreshRequired;
      externalRefreshPending = false;
      setBusy(true);
      if (errorNotice) errorNotice.hidden = true;
      try {
        do {
          repeat = false;
          await waitForIdle();
          const started = revision;
          const result = window.navigator.locks
            ? await window.navigator.locks.request('solid-olive-free-tote', () => controller.reconcile())
            : await controller.reconcile();
          announce = announce || result.added;
          pendingAnnouncement = announce;
          refreshRequired = refreshRequired || result.changed;
          needsRefresh = needsRefresh || result.changed || externalRefreshPending;
          externalRefreshPending = false;
          if (started !== revision || activeRequests) {
            needsRefresh = true;
            repeat = true;
            continue;
          }
          if (needsRefresh) {
            result.cart = await refreshCart(result.cart);
            needsRefresh = false;
            refreshRequired = false;
          }
          if (started !== revision || activeRequests) {
            needsRefresh = true;
            repeat = true;
          }
          if (!controller.hasFreeTote(result.cart)) {
            announce = false;
            pendingAnnouncement = false;
            if (result.cart.items.some(controller.isManaged)) {
              repeat = true;
              needsRefresh = true;
            }
          }
        } while (repeat);
        if (toast && announce) {
          toast.hidden = false;
          clearPending();
        }
        pendingAnnouncement = false;
      } catch (error) {
        refreshRequired = true;
        if (toast) toast.hidden = true;
        if (errorNotice) errorNotice.hidden = false;
        throw error;
      } finally {
        setBusy(false);
        running = undefined;
      }
    })();
    return running;
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(() => sync().catch(() => {}), 120);
  }

  window.fetch = function (input, options) {
    let mutation = false;
    try {
      const url = new URL(typeof input === 'string' || input instanceof URL ? input : input.url, window.location.href);
      const method = options?.method || input?.method || 'GET';
      mutation = url.origin === window.location.origin && /\/cart\/(add|change|update|clear)(\.js)?\/?$/.test(url.pathname) && method.toUpperCase() !== 'GET';
    } catch (error) {}
    if (!mutation) return originalFetch(input, options);
    activeRequests += 1;
    revision += 1;
    if (toast) toast.hidden = true;
    return originalFetch(input, options).finally(() => {
      activeRequests -= 1;
      externalRefreshPending = true;
      if (!activeRequests) {
        idleWaiters.forEach((resolve) => resolve());
        idleWaiters.clear();
      }
      schedule();
    });
  };

  async function continueCheckout(action) {
    if (checkoutPending) return;
    checkoutPending = true;
    try {
      await sync();
      passingCheckout = true;
      try { action(); } finally { passingCheckout = false; }
    } catch (error) {} finally {
      checkoutPending = false;
    }
  }

  document.addEventListener('click', (event) => {
    if (passingCheckout || !event.target.closest) return;
    const button = event.target.closest('button[name="checkout"], input[name="checkout"]');
    const link = event.target.closest('a[href]');
    let checkoutLink = false;
    if (link) {
      const url = new URL(link.href, window.location.href);
      checkoutLink = url.origin === window.location.origin && /^\/checkout(s)?(\/|$)/.test(url.pathname);
    }
    if (!button && !checkoutLink) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    continueCheckout(() => {
      if (!button) {
        window.location.assign(link.href);
        return;
      }
      const current = button.isConnected ? button : document.getElementById(button.id) ||
        document.querySelector('button[name="checkout"], input[name="checkout"]');
      current?.click();
    });
  }, true);

  document.addEventListener('submit', (event) => {
    if (passingCheckout || event.submitter?.name !== 'checkout') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const form = event.target;
    const button = event.submitter;
    continueCheckout(() => {
      const current = button.isConnected ? button : document.getElementById(button.id) ||
        document.querySelector('button[name="checkout"], input[name="checkout"]');
      if (current && form.isConnected) form.requestSubmit(current);
    });
  }, true);

  toast?.querySelector('button')?.addEventListener('click', () => { toast.hidden = true; });
  errorNotice?.querySelector('button')?.addEventListener('click', () => window.location.reload());
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      externalRefreshPending = true;
      schedule();
    });
  }
  window.addEventListener('pageshow', (event) => { if (event.persisted) schedule(); });
  window.solidOliveFreeTote = { sync };
  if (config.initialItemCount > 0) sync().catch(() => {});
})();
